import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, useAuthActions } from '@/stores/auth';
import { isLegalAdminOnly } from '@/utils/access';
import { SECURITY_DASHBOARD_PATH } from '@/config/relocatedRoutes';

interface SecurityModuleRouteProps {
  /** The Corporate Security page to render when access is granted. */
  children: ReactNode;
  /**
   * Optional permission required in addition to security-module access (e.g.
   * Analytics passes `'analytics:read'`). Checked via the store's wildcard- and
   * SUPER_ADMIN-aware `checkPermission`, not a raw `permissions.includes`.
   */
  requiredPermission?: string;
}

/**
 * VIS-150 route guard for relocated Corporate Security surfaces (`/security/*`).
 *
 * Mirrors the navigation-hide rules at the route layer:
 *  - LEGAL_ADMIN-only users (VIS-132) are redirected to their Legal & Compliance
 *    home — they must see neither the security dashboard nor analytics.
 *  - When `requiredPermission` is set, users lacking it are sent back to the
 *    security dashboard (which carries no extra permission requirement).
 *
 * Authentication itself is already enforced by `AppLayout`, which only renders
 * the routed `Outlet` for authenticated users.
 */
export default function SecurityModuleRoute({
  children,
  requiredPermission,
}: SecurityModuleRouteProps) {
  const { user } = useAuth();
  const { checkPermission } = useAuthActions();

  const roleNames = user?.roles.map((r) => r.name) ?? [];

  if (isLegalAdminOnly(roleNames)) {
    return <Navigate to="/legal" replace />;
  }

  if (requiredPermission && !checkPermission(requiredPermission)) {
    return <Navigate to={SECURITY_DASHBOARD_PATH} replace />;
  }

  return <>{children}</>;
}
